import { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Plus,
  Trash2,
  Building2,
  Globe,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, SlideInRight } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { PortfolioClaim } from '@/lib/state/inverio-store';
import { SkipButton } from '@/components/SkipButton';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAuth, isSupabaseConfigured } from '@/lib/supabase';
import { filePortfolioClaims } from '@/lib/supabase/portfolio-verification';
import { reportError } from '@/lib/errors';

interface NewCompany {
  companyName: string;
  website: string;
  founderEmail: string;
}

const getStatusConfig = (colors: ReturnType<typeof useSystemThemeColors>): Record<
  PortfolioClaim['status'],
  { label: string; icon: typeof Clock; color: string; bgColor: string }
> => ({
  pending: {
    label: 'Pending',
    icon: Clock,
    color: colors.amber.main,
    bgColor: colors.amber.light,
  },
  verified: {
    label: 'Verified',
    icon: CheckCircle,
    color: colors.traffic.green,
    bgColor: colors.traffic.greenLight,
  },
  rejected: {
    label: 'Rejected',
    icon: AlertCircle,
    color: colors.traffic.red,
    bgColor: colors.traffic.redLight,
  },
});

export default function PortfolioCompaniesScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const STATUS_CONFIG = getStatusConfig(colors);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  // AUTH user id — portfolio_verification_requests.investor_user_id is
  // auth.users(id), matching portfolio_companies and investor_track_record.
  const { user } = useAuth();
  const storeUser = useProfileStore((s) => s.user);
  const skipOnboardingStep = useProfileStore((s) => s.skipOnboardingStep);

  const [companies, setCompanies] = useState<PortfolioClaim[]>(
    investorProfile?.portfolioClaims ?? []
  );
  const [showForm, setShowForm] = useState(companies.length === 0);
  const [newCompany, setNewCompany] = useState<NewCompany>({
    companyName: '',
    website: '',
    founderEmail: '',
  });

  const addCompany = () => {
    if (!newCompany.companyName.trim()) return;

    const claim: PortfolioClaim = {
      id: Date.now().toString(),
      companyName: newCompany.companyName.trim(),
      website: newCompany.website.trim() || undefined,
      founderEmail: newCompany.founderEmail.trim() || undefined,
      claimDate: new Date().toISOString(),
      status: newCompany.founderEmail.trim() ? 'pending' : 'pending',
    };

    setCompanies([...companies, claim]);
    setNewCompany({
      companyName: '',
      website: '',
      founderEmail: '',
    });
    setShowForm(false);
  };

  const removeCompany = (id: string) => {
    setCompanies(companies.filter((c) => c.id !== id));
  };

  const handleContinue = () => {
    // Track as skipped if no companies added
    if (companies.length === 0) {
      skipOnboardingStep('portfolio');
    }

    // Update investor profile
    if (investorProfile) {
      setInvestorProfile({
        ...investorProfile,
        portfolioClaims: companies,
        portfolioCount: companies.length,
      });
    }

    // File the claims on the SERVER.
    //
    // Until now this step wrote `portfolioClaims` to the local store and
    // nothing else, while the founder-facing queue
    // ((startup)/verification-requests.tsx) read a mock array. Both ends of
    // portfolio verification existed and nothing connected them, so a claim
    // could never reach the founder it named — the feature only worked in mock
    // mode.
    //
    // Non-blocking so navigation stays instant, but NOT silent: the claims
    // remain in the local store and a failure is reported rather than
    // swallowed.
    if (user?.id && isSupabaseConfigured() && companies.length > 0) {
      // Attribution comes from the track record captured in the EARLIER
      // onboarding step, matched by company name. The founder answering the
      // claim needs a name and a date to recognise the deal — and for a
      // syndicate, the LEAD they actually dealt with, since a syndicate
      // participant is usually a name they have never seen.
      const trackRecord = investorProfile?.investmentHistory ?? [];
      const matchFor = (companyName: string) =>
        trackRecord.find(
          (r) => r.companyName.trim().toLowerCase() === companyName.trim().toLowerCase(),
        );

      void filePortfolioClaims(
        user.id,
        // The founder must be able to attribute the claim. Falls back to the
        // email rather than filing an anonymous claim nobody can answer.
        storeUser?.name?.trim() || user.email || '',
        companies.map((c) => {
          const match = matchFor(c.companyName);
          return {
            companyName: c.companyName,
            website: c.website,
            founderEmail: c.founderEmail,
            investmentType: match?.investmentType,
            syndicateLeadName: match?.syndicateLeadName,
            investmentDate: match?.date,
          };
        }),
      ).catch((err) => {
        reportError(err, {
          context: 'PortfolioCompaniesOnboarding.fileClaims',
          level: 'warning',
        });
      });
    }

    router.push('/(onboarding)/complete');
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canAdd =
    newCompany.companyName.trim() &&
    (!newCompany.founderEmail.trim() || isValidEmail(newCompany.founderEmail.trim()));

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-row gap-2">
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.border.light }}
              />
            </View>
            <View className="w-10" />
          </View>
        </Animated.View>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          <ScrollView className="flex-1 px-6" showsVerticalScrollIndicator={false}>
            {/* Title */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(600)}
              className="mb-6"
            >
              <View className="flex-row items-center gap-3 mb-2">
                <View
                  className="w-12 h-12 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <Building2 size={24} color={colors.primary[600]} />
                </View>
                <View className="flex-1">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-xl font-bold"
                  >
                    Portfolio Companies
                  </Text>
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm"
                  >
                    Add companies you've invested in
                  </Text>
                </View>
              </View>
            </Animated.View>

            {/* Company List */}
            {companies.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="mb-4"
              >
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-sm font-medium mb-3"
                >
                  Your Portfolio ({companies.length})
                </Text>
                <View className="gap-3">
                  {companies.map((company, index) => {
                    const statusConfig = STATUS_CONFIG[company.status];
                    const StatusIcon = statusConfig.icon;

                    return (
                      <Animated.View
                        key={company.id}
                        entering={SlideInRight.delay(index * 100).duration(400)}
                      >
                        <View
                          className="p-4 rounded-xl"
                          style={{
                            backgroundColor: colors.surface.primary,
                            borderWidth: 1,
                            borderColor: colors.border.light,
                          }}
                        >
                          <View className="flex-row items-start">
                            <View
                              className="w-10 h-10 rounded-full items-center justify-center mr-3"
                              style={{ backgroundColor: colors.primary[50] }}
                            >
                              <Building2 size={18} color={colors.primary[600]} />
                            </View>
                            <View className="flex-1">
                              <Text
                                style={{ color: colors.text.primary }}
                                className="font-semibold text-sm mb-1"
                              >
                                {company.companyName}
                              </Text>
                              {company.website && (
                                <View className="flex-row items-center gap-1 mb-1">
                                  <Globe size={12} color={colors.text.tertiary} />
                                  <Text
                                    style={{ color: colors.text.tertiary }}
                                    className="text-xs"
                                  >
                                    {company.website}
                                  </Text>
                                </View>
                              )}
                              {company.founderEmail && (
                                <View className="flex-row items-center gap-1">
                                  <Mail size={12} color={colors.text.tertiary} />
                                  <Text
                                    style={{ color: colors.text.tertiary }}
                                    className="text-xs"
                                  >
                                    {company.founderEmail}
                                  </Text>
                                </View>
                              )}
                            </View>
                            <View className="flex-row items-center gap-2">
                              <View
                                className="flex-row items-center gap-1 px-2 py-1 rounded-full"
                                style={{ backgroundColor: statusConfig.bgColor }}
                              >
                                <StatusIcon size={12} color={statusConfig.color} />
                                <Text
                                  style={{ color: statusConfig.color }}
                                  className="text-xs font-medium"
                                >
                                  {statusConfig.label}
                                </Text>
                              </View>
                              <Pressable
                                onPress={() => removeCompany(company.id)}
                                className="p-1"
                              >
                                <Trash2 size={16} color={colors.traffic.red} />
                              </Pressable>
                            </View>
                          </View>
                        </View>
                      </Animated.View>
                    );
                  })}
                </View>
              </Animated.View>
            )}

            {/* Add Company Form */}
            {showForm ? (
              <Animated.View
                entering={FadeInUp.duration(400)}
                className="mb-6"
              >
                <View
                  className="p-5 rounded-xl"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.primary[300],
                  }}
                >
                  <Text
                    style={{ color: colors.text.primary }}
                    className="font-semibold mb-4"
                  >
                    Add Portfolio Company
                  </Text>

                  {/* Company Name */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Company Name *
                    </Text>
                    <TextInput
                      value={newCompany.companyName}
                      onChangeText={(text) =>
                        setNewCompany({ ...newCompany, companyName: text })
                      }
                      placeholder="e.g., Acme Inc."
                      placeholderTextColor={colors.text.tertiary}
                      className="px-4 py-3 rounded-xl text-base"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        color: colors.text.primary,
                      }}
                    />
                  </View>

                  {/* Website */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Website (optional)
                    </Text>
                    <View className="flex-row items-center">
                      <View
                        className="px-4 py-3 rounded-l-xl border-r"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          borderColor: colors.border.light,
                        }}
                      >
                        <Globe size={18} color={colors.text.tertiary} />
                      </View>
                      <TextInput
                        value={newCompany.website}
                        onChangeText={(text) =>
                          setNewCompany({ ...newCompany, website: text })
                        }
                        placeholder="www.example.com"
                        placeholderTextColor={colors.text.tertiary}
                        autoCapitalize="none"
                        keyboardType="url"
                        className="flex-1 px-4 py-3 rounded-r-xl text-base"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          color: colors.text.primary,
                        }}
                      />
                    </View>
                  </View>

                  {/* Founder Email */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Founder Email (for verification)
                    </Text>
                    <View className="flex-row items-center">
                      <View
                        className="px-4 py-3 rounded-l-xl border-r"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          borderColor: colors.border.light,
                        }}
                      >
                        <Mail size={18} color={colors.text.tertiary} />
                      </View>
                      <TextInput
                        value={newCompany.founderEmail}
                        onChangeText={(text) =>
                          setNewCompany({ ...newCompany, founderEmail: text })
                        }
                        placeholder="founder@example.com"
                        placeholderTextColor={colors.text.tertiary}
                        autoCapitalize="none"
                        keyboardType="email-address"
                        className="flex-1 px-4 py-3 rounded-r-xl text-base"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          color: colors.text.primary,
                        }}
                      />
                    </View>
                    <Text
                      style={{ color: colors.text.tertiary }}
                      className="text-xs mt-2"
                    >
                      If provided, we'll ask the founder to verify your investment
                    </Text>
                  </View>

                  {/* Form Actions */}
                  <View className="flex-row gap-3">
                    <AnimatedPressable
                      onPress={() => setShowForm(false)}
                      className="flex-1 py-3 rounded-xl items-center"
                      style={{
                        backgroundColor: colors.surface.secondary,
                      }}
                    >
                      <Text
                        style={{ color: colors.text.secondary }}
                        className="font-medium"
                      >
                        Cancel
                      </Text>
                    </AnimatedPressable>
                    <AnimatedPressable
                      onPress={addCompany}
                      disabled={!canAdd}
                      className="flex-1 py-3 rounded-xl items-center"
                      style={{
                        backgroundColor: canAdd
                          ? colors.primary[600]
                          : colors.border.light,
                      }}
                    >
                      <Text
                        style={{
                          color: canAdd ? 'white' : colors.text.tertiary,
                        }}
                        className="font-medium"
                      >
                        Add
                      </Text>
                    </AnimatedPressable>
                  </View>
                </View>
              </Animated.View>
            ) : (
              <Animated.View
                entering={FadeInUp.delay(400).duration(500)}
                className="mb-6"
              >
                <AnimatedPressable
                  onPress={() => setShowForm(true)}
                  className="flex-row items-center justify-center gap-2 py-4 rounded-xl"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                    borderStyle: 'dashed',
                  }}
                >
                  <Plus size={20} color={colors.primary[600]} />
                  <Text
                    style={{ color: colors.primary[600] }}
                    className="font-medium"
                  >
                    Add Company
                  </Text>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Info Box */}
            <Animated.View
              entering={FadeInUp.delay(500).duration(500)}
              className="mb-6"
            >
              <View
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-xs leading-5"
                >
                  💡 When you add a founder's email, they'll receive a verification
                  request. Once confirmed, your investment will be marked as verified
                  and visible on your profile.
                </Text>
              </View>
            </Animated.View>

            <View className="h-8" />
          </ScrollView>
        </KeyboardAvoidingView>

        {/* Continue Button */}
        <Animated.View
          entering={FadeInUp.delay(600).duration(500)}
          className="px-5 pb-8"
        >
          <AnimatedPressable
            onPress={handleContinue}
            className="rounded-xl py-4 flex-row items-center justify-center gap-2"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text
              style={{ color: colors.text.inverse }}
              className="font-semibold text-base"
            >
              {companies.length > 0 ? 'Continue' : 'Skip for now'}
            </Text>
          </AnimatedPressable>
        </Animated.View>
      </SafeAreaView>
    </View>
  );
}
